import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Divider,
  Flex,
  Switch,
  Table,
  TableColumnsType,
  Typography
} from 'antd'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceGetIcd10AmClassifierElementListEndpoint,
  useClassifierServiceGetIcd10AmClassifierElementListEndpointKey,
  useClassifierServiceSetIcd10AmTypeEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  GetIcd10AmClassifierElementListResponseItem,
  Icd10AmElementType
} from 'src/api/requests'
import { message, PageBreadcrumbs, SearchBar } from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

/**
 * ICD-10-AM elements marked as opportunistic diseases, i.e. the diseases listed in field 56
 * of Form 058-1/e. ICD-10-AM stays out of the generic classifier editor.
 */
export const Icd10OpportunisticPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pagination = useTablePagination()

  const [search, setSearch] = useState('')
  const [onlyMarked, setOnlyMarked] = useState(false)
  const [pendingId, setPendingId] = useState<string>()

  const classifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const { data, isLoading, isFetching } =
    useClassifierServiceGetIcd10AmClassifierElementListEndpoint(
      {
        id: String(classifierId),
        pageNumber: Number(pagination.current),
        pageSize: Number(pagination.pageSize),
        ...(!!search && { searchTerm: search }),
        ...(onlyMarked && { type: Icd10AmElementType.OPPORTUNISTIC_DISEASE })
      },
      undefined,
      { enabled: !!classifierId }
    )

  const { mutate: setType } = useClassifierServiceSetIcd10AmTypeEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          useClassifierServiceGetIcd10AmClassifierElementListEndpointKey
        ]
      })
      message.success(t('common.save_success'))
    },
    onError: (e: ApiError) => formErrorHandler(e.body?.errors),
    onSettled: () => setPendingId(undefined)
  })

  const onToggle = (elementId: string, checked: boolean) => {
    setPendingId(elementId)
    setType({
      id: String(classifierId),
      elementId,
      requestBody: {
        type: checked ? Icd10AmElementType.OPPORTUNISTIC_DISEASE : null
      }
    })
  }

  const columns: TableColumnsType<GetIcd10AmClassifierElementListResponseItem> =
    [
      {
        key: 'code',
        title: t('common.code'),
        dataIndex: 'code',
        width: '15%'
      },
      {
        key: 'name',
        title: t('classifiers.title'),
        render: (_, { translation }) => translation?.[currentLang.value],
        width: '60%'
      },
      {
        key: 'type',
        title: t('classifiers.opportunistic_disease_toggle'),
        align: 'center',
        render: (_, { id, type }) => (
          <Switch
            checked={type === Icd10AmElementType.OPPORTUNISTIC_DISEASE}
            loading={pendingId === id}
            disabled={!!pendingId && pendingId !== id}
            onChange={(checked) => onToggle(String(id), checked)}
          />
        ),
        width: '25%'
      }
    ]

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const onOnlyMarkedChange = (checked: boolean) => {
    setOnlyMarked(checked)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('classifiers.opportunistic_diseases')}
      items={[
        {
          title: t('classifiers.classifiers'),
          path: paths.classifiers.root
        },
        { title: t('classifiers.opportunistic_diseases') }
      ]}
    >
      <Flex vertical gap={24}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={() => navigate(paths.classifiers.root)}
        >
          {t('common.back')}
        </Button>

        <Flex vertical>
          <Flex justify="space-between" align="center" gap={16} wrap>
            <SearchBar onSearch={onSearch} />

            <Flex gap={8} align="center">
              <Switch checked={onlyMarked} onChange={onOnlyMarkedChange} />
              <Typography.Text>{t('classifiers.only_marked')}</Typography.Text>
            </Flex>
          </Flex>

          <Divider />

          <Table
            bordered
            rowKey="id"
            columns={columns}
            loading={isLoading || (isFetching && !pendingId)}
            dataSource={data?.data ?? []}
            scroll={{ x: 'max-content' }}
            pagination={{
              ...pagination,
              total: data?.totalCount
            }}
          />
        </Flex>
      </Flex>
    </PageBreadcrumbs>
  )
}
